import { usePersonReadDataContext } from '@aibees/elephant/context';
import { ParentFeature } from '@aibees/elephant/feature/search-table-people/components/people-drawer/components/contact-details/context';
import { ContactDetailsProvider } from '@aibees/elephant/feature/search-table-people/components/people-drawer/components/contact-details/context/provider';
import { useGetPersonUpdatesData } from '@aibees/elephant/hooks/people/use-get-person-updates-data';
import { useResetPersonUpdatesStoreOnUnmount } from '@aibees/elephant/hooks/people/use-reset-person-updates-store-on-unmount';
import { Icon, Tab } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { TabContext, TabList } from '@mui/lab';
import { Box, Card, Divider, Typography } from '@mui/material';
import { memo, useEffect, useMemo, useState } from 'react';
import { PersonUpdatesContact } from './components/person-updates-contact';
import { PersonUpdatesPersonalityAnalysis } from './components/person-updates-personality-analysis';
import { PersonUpdatesProfileDetails } from './components/person-updates-profile-details';
import * as Styled from './styles';
import * as SX from './sx';
import {
  PeopleUpdatesTabInfo,
  PeopleUpdatesTabs,
  PeopleSidebarUpdatesProps as Props,
} from './types';

export const PersonSidebarUpdates = memo(({ className }: Props) => {
  const { api } = usePersonReadDataContext();
  const { profileUpdatesCount, emailUpdatesCount, aiAnalysisUpdatesCount } =
    useGetPersonUpdatesData(api?.data);
  const [activeTab, setActiveTab] = useState<PeopleUpdatesTabs>();
  useResetPersonUpdatesStoreOnUnmount();

  // initializing default active tab
  useEffect(() => {
    if (!activeTab && !api?.isFetching) {
      if (profileUpdatesCount) setActiveTab(PeopleUpdatesTabs.Insight);
      else if (emailUpdatesCount) setActiveTab(PeopleUpdatesTabs.Contact);
      else if (aiAnalysisUpdatesCount) setActiveTab(PeopleUpdatesTabs.Analysis);
      else setActiveTab(PeopleUpdatesTabs.Insight);
    }
  }, [
    profileUpdatesCount,
    emailUpdatesCount,
    aiAnalysisUpdatesCount,
    api?.isFetching,
  ]);

  const tabs: PeopleUpdatesTabInfo[] = useMemo(
    () => [
      {
        title: PeopleUpdatesTabs.Insight,
        icon: 'badge-outline',
        content: (
          <ContactDetailsProvider
            parentFeature={ParentFeature.PERSON_SIDEBAR_UPDATES}
            key={api?.data?.id}
          >
            <PersonUpdatesProfileDetails />
          </ContactDetailsProvider>
        ),
        count: profileUpdatesCount,
      },
      {
        title: PeopleUpdatesTabs.Contact,
        icon: 'email',
        content: <PersonUpdatesContact />,
        count: emailUpdatesCount,
      },
      {
        title: PeopleUpdatesTabs.Analysis,
        icon: 'person-search-outline',
        content: <PersonUpdatesPersonalityAnalysis />,
        count: aiAnalysisUpdatesCount,
      },
    ],
    [
      api?.data?.id,
      profileUpdatesCount,
      emailUpdatesCount,
      aiAnalysisUpdatesCount,
    ]
  );

  const content = useMemo(
    () => tabs.find((t) => t.title === activeTab)?.content,
    [tabs, activeTab]
  );

  const handleChangeTab = (
    event: React.SyntheticEvent,
    newValue: PeopleUpdatesTabs
  ) => {
    setActiveTab(newValue);
  };

  const renderTabTitle = (tabInfo: PeopleUpdatesTabInfo) => {
    return (
      <Box display="flex" gap={0.5} alignItems={'center'}>
        <Icon size={16} name={tabInfo.icon} />
        <Typography variant="body3" fontWeight={600}>
          {tabInfo.title}
        </Typography>
      </Box>
    );
  };

  return (
    <Styled.Wrapper
      className={classnames('PersonSidebarUpdates', className)}
      sx={SX.root}
    >
      <Card variant="outlined" headerDivider>
        {activeTab && (
          <TabContext value={activeTab.toString()}>
            <TabList
              onChange={handleChangeTab}
              variant="fullWidth"
              size="large"
            >
              {tabs.map((tab) => (
                <Tab
                  LabelContent={
                    <Typography variant="body4">{tab.count}</Typography>
                  }
                  LabelVariant="soft"
                  key={tab.title}
                  label={renderTabTitle(tab)}
                  value={tab.title}
                />
              ))}
            </TabList>
          </TabContext>
        )}
        <Divider />
        <Box p={1}>{content}</Box>
      </Card>
    </Styled.Wrapper>
  );
});

PersonSidebarUpdates.displayName = 'PersonSidebarUpdates';
